import React, { useState, useCallback, useEffect } from 'react'
import Modal from 'lib/components/modals/modal'
import Button from 'components/core/button'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ExternalLinkIcon,
  Loader2Icon,
  LucideLoaderCircle,
  SettingsIcon,
  XIcon,
} from 'lucide-react'
import { useRequestContext } from 'components/pages/request/providers/request-provider'
import {
  Collaborator,
  DesignRequest,
  getCollaborators,
  inviteCollaborators,
  shareTicket,
  updateShareLink,
} from 'lib/api/tickets/tickets'
import CopyButton from './share-modal-copy-button'
import ShareToggle from './share-modal-toggle'
import CustomizeLink from './share-modal-customize-link'
import CollaboratorsList from './share-modal-collaborators-list'
import InviteCollaboratorsInput from './share-modal-invite-input'
import { toast } from 'lib/components/toast/toast'
import IconButton from 'lib/components/buttons/icon-button'
import Checkbox from 'lib/components/checkbox/checkbox'
import { useQuery } from '@tanstack/react-query'
import { getTicketFiles, TicketFile } from 'lib/api/ticket-files/ticket-files'
import { getPreviewUrl } from 'lib/util/file/file'
import Tooltip from 'lib/components/tooltip/tooltip'
import { cn } from 'lib/util/cn'

interface ShareModalProps {
  open: boolean
  setOpen: (open: boolean) => void
}

const generateShareUrl = (origin: string, segments: string[], shareId: string) => {
  const filteredSegments = segments.filter(Boolean)
  const segmentPath = filteredSegments.length > 0 ? `/${filteredSegments.join('/')}` : ''
  return `${origin}/share${segmentPath}/${shareId ?? ''}`
}

const useShareLink = (ticket: DesignRequest) => {
  const { setTicket } = useRequestContext()
  const [isShared, setIsShared] = useState((ticket.meta.sharing.id && ticket.meta.sharing.available) || false)
  const [segments, setSegments] = useState(ticket.meta.sharing?.segments ?? [])
  const [isCustomDomainEnabled, setIsCustomDomainEnabled] = useState(ticket.meta.sharing.customDomain != null)
  const [selectedAssets, setSelectedAssets] = useState<TicketFile['id'][]>([])
  const [hasInitialized, setHasInitialized] = useState(false)

  const {
    data: ticketFiles,
    isLoading: isLoadingFiles,
    refetch,
  } = useQuery({
    queryKey: ['collaborator-visible-ticket-files', ticket.id],
    queryFn: () => getTicketFiles(ticket.id, { viewAs: 'collaborator_admin' }),
    enabled: !!ticket.id,
  })

  useEffect(() => {
    if (ticketFiles && !hasInitialized) {
      const sharedAssetIds = ticketFiles.filter((file) => file.shared === true).map((file) => file.id)
      setSelectedAssets(sharedAssetIds)
      setHasInitialized(true)
    }
  }, [ticketFiles, hasInitialized])

  const toggleShare = useCallback(async () => {
    try {
      setIsShared(!isShared)
      const response = await shareTicket({ ticketId: ticket.id, enabled: !isShared })
      setTicket((prevTicket) => {
        // Type guard to check if the response has the expected structure
        if ('ticket' in response && 'meta' in response.ticket && 'sharing' in response.ticket.meta) {
          return {
            ...prevTicket,
            meta: {
              ...prevTicket.meta,
              sharing: {
                ...prevTicket.meta.sharing,
                ...response.ticket.meta.sharing,
              },
            },
          } as DesignRequest
        } else {
          console.warn('Unexpected response structure from shareTicket')
          return prevTicket
        }
      })
      toast.success(isShared ? 'Sharing disabled' : 'Sharing enabled')
    } catch (error) {
      setIsShared(isShared)
      toast.error('Failed to toggle sharing')
      console.error('Failed to toggle sharing ', error)
    }
  }, [isShared, ticket.id, setTicket])

  const updateSharedTicketFiles = useCallback(async () => {
    try {
      const response = await shareTicket({
        ticketId: ticket.id,
        enabled: isShared,
        excludedTicketFileIds: ticketFiles?.filter((file) => !selectedAssets.includes(file.id)).map((file) => file.id),
      })
      console.log('UpdateSharedTicketFiles response: ', response)
      await refetch()
      toast.success('Invite settings updated')
    } catch (error) {
      toast.error('Failed to update invite settings')
      console.error('Failed to update invite settings ', error)
    }
  }, [ticket.id, ticketFiles, refetch, selectedAssets, isShared])

  const updateSegments = useCallback(
    async (newSegments: string[]) => {
      const oldSegments = segments
      try {
        setSegments(newSegments)
        const response = await updateShareLink(String(ticket.id), ticket.meta.sharing.id, {
          share: { segments: newSegments },
        })
        setTicket(
          (prevTicket) =>
            ({
              ...prevTicket,
              meta: {
                ...prevTicket.meta,
                sharing: {
                  ...prevTicket.meta.sharing,
                  ...(response.ticket?.meta?.sharing ?? response.meta?.sharing ?? {}),
                },
              },
            }) as DesignRequest,
        )
        toast.success('Custom link updated')
      } catch (error) {
        setSegments(oldSegments)
        toast.error('Failed to update custom link')
        console.error('Failed to update custom link ', error)
      }
    },
    [ticket.id, ticket.meta.sharing.id, setTicket, segments],
  )

  return {
    isShared,
    segments,
    selectedAssets,
    setSelectedAssets,
    isCustomDomainEnabled,
    toggleShare,
    updateSegments,
    updateSharedTicketFiles,
    setIsCustomDomainEnabled,
    ticketFiles,
    isLoadingFiles,
  }
}

type ShareModalPage = 'default' | 'settings'

export default function ShareModal({ open, setOpen }: ShareModalProps) {
  const [isLoading, setIsLoading] = useState(true)
  const { ticket } = useRequestContext()
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const [invitedCollaborators, setInvitedCollaborators] = useState<Collaborator['email'][]>([])
  const [page, setPage] = useState<ShareModalPage>('default')

  function setCollaboratorState(collaborators: Collaborator[]) {
    setCollaborators(collaborators.filter((c) => c.id != null))
    setInvitedCollaborators(collaborators.filter((c) => c.id == null).map((c) => c.email))
  }

  useEffect(() => {
    const fetchCollaborators = async () => {
      setIsLoading(true)
      try {
        const data = await getCollaborators(String(ticket.id))
        setCollaboratorState(data.data)
      } catch (error) {
        toast.error('Failed to load collaborators, please refresh the page.')
        console.error('Failed to load collaborators, please refresh the page. ', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchCollaborators()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal open={open} setOpen={setOpen} size="md">
      <div className="tw-relative">
        {isLoading && (
          <div
            className="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-white"
            data-testid="share-modal-loader"
          >
            <LucideLoaderCircle className="tw-h-10 tw-w-10 tw-animate-spin tw-opacity-50" />
          </div>
        )}
        <span className="flash_message_holder tw-mx-20" />
        <Modal.Body setOpen={setOpen}>
          {page === 'default' && (
            <ShareModalDefaultPage
              collaborators={collaborators}
              invitedCollaborators={invitedCollaborators}
              setCollaboratorState={setCollaboratorState}
              setOpen={setOpen}
              setPage={setPage}
            />
          )}
          {page === 'settings' && <ShareModalSettingsPage setPage={setPage} />}
        </Modal.Body>
      </div>
    </Modal>
  )
}

const ShareModalDefaultPage = ({
  collaborators,
  invitedCollaborators,
  setCollaboratorState,
  setOpen,
  setPage,
}: {
  collaborators: Collaborator[]
  invitedCollaborators: Collaborator['email'][]
  setCollaboratorState: (collaborators: Collaborator[]) => void
  setOpen: (open: boolean) => void
  setPage: (page: ShareModalPage) => void
}) => {
  const { ticket } = useRequestContext()
  const [isSending, setIsSending] = useState(false)
  const {
    isShared,
    segments,
    isCustomDomainEnabled,
    toggleShare,
    updateSegments,
    selectedAssets,
    ticketFiles,
    isLoadingFiles,
  } = useShareLink(ticket)
  const [pendingInvitedCollaborators, setPendingInvitedCollaborators] = useState<Collaborator['email'][]>([])

  async function handleSend() {
    setIsSending(true)
    try {
      const data = await inviteCollaborators(String(ticket.id), pendingInvitedCollaborators)
      setCollaboratorState(data.data)
      setPendingInvitedCollaborators([])
      toast.success('Invites sent!')
    } catch (error) {
      toast.error('Failed to load collaborators, please refresh the page.')
      console.error('Failed to load collaborators, please refresh the page. ', error)
    } finally {
      setIsSending(false)
    }
  }

  const shareUrl = generateShareUrl(
    ticket.meta.sharing?.customDomain?.domain ?? window.location.origin,
    segments ?? [''],
    ticket.meta.sharing.id,
  )

  return (
    <div>
      <div className="tw-flex tw-justify-between tw-gap-4">
        <h4 className="tw-m-0">Invite Collaborators</h4>
        <div className="tw-flex tw-gap-2">
          {isShared && ticketFiles?.length > 0 && (
            <IconButton color="secondary" onClick={() => setPage('settings')}>
              <SettingsIcon />
            </IconButton>
          )}
          <IconButton color="secondary" onClick={() => setOpen(false)}>
            <XIcon />
          </IconButton>
        </div>
      </div>
      <ShareToggle isShared={isShared} onToggle={toggleShare} />
      <div className={cn('tw-mt-4', !isShared && 'tw-pointer-events-none tw-opacity-30 tw-saturate-0 tw-filter')}>
        <div className="tw-flex tw-h-14 tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-bg-neutral-100 tw-p-2">
          {isLoadingFiles && <Loader2Icon className="tw-h-6 tw-w-6 tw-animate-spin tw-text-neutral-400" />}
          {ticketFiles?.length > 0 && !isLoadingFiles && (
            <>
              {ticketFiles.length === selectedAssets.length
                ? 'All assets included in Share Link'
                : `${selectedAssets.length} assets included in Share Link`}
              <span
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-text-cornflower-500 hover:tw-underline"
                onClick={() => setPage('settings')}
              >
                Edit <ArrowRightIcon className="lu-sm" />
              </span>
            </>
          )}
        </div>
        <InviteCollaboratorsInput
          allCollaborators={[...collaborators.map((c) => c.email), ...invitedCollaborators]}
          collaborators={pendingInvitedCollaborators}
          setCollaborators={setPendingInvitedCollaborators}
          className="tw-my-6"
        />
        <CollaboratorsList
          className="tw-mt-4"
          collaborators={collaborators}
          invitedCollaborators={invitedCollaborators}
        />
        <div className="tw-my-6 tw-border-0 tw-border-b tw-border-solid" />
      </div>
      <CustomizeLink
        className="tw-mt-4"
        shareUrl={shareUrl}
        currentSegments={segments}
        onUpdateSegments={updateSegments}
        isCustomDomainEnabled={isCustomDomainEnabled}
      />
      <div className={`${!isShared ? 'tw-pointer-events-none tw-opacity-30 tw-saturate-0 tw-filter' : ''}`}>
        <div className="tw-mt-6 tw-flex tw-items-center tw-justify-between">
          <CopyButton isShared={isShared} shareUrl={shareUrl} />
          <Button
            color="purple"
            type="button"
            onClick={handleSend}
            disabled={pendingInvitedCollaborators.length === 0 || isSending}
            className="tw-flex tw-items-center tw-gap-2"
          >
            {isSending && <LucideLoaderCircle className="tw-animate-spin" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  )
}

const ShareModalSettingsPage = ({ setPage }: { setPage: (page: ShareModalPage) => void }) => {
  const { ticket } = useRequestContext()
  const { selectedAssets, setSelectedAssets, ticketFiles, isLoadingFiles, updateSharedTicketFiles } =
    useShareLink(ticket)

  const [isUpdating, setIsUpdating] = useState(false)

  const toggleAssetSelection = (assetId: number) => {
    if (selectedAssets.includes(assetId)) {
      setSelectedAssets(selectedAssets.filter((id) => id !== assetId))
    } else {
      setSelectedAssets([...selectedAssets, assetId])
    }
  }

  const selectAllAssets = () => {
    if (selectedAssets.length === ticketFiles?.length || ticketFiles === undefined) {
      setSelectedAssets([])
    } else {
      setSelectedAssets(ticketFiles.map((asset) => asset.id))
    }
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-8">
      {isLoadingFiles && <Loader2Icon className="tw-h-10 tw-w-10 tw-animate-spin" />}
      <div className="tw-flex tw-items-center tw-gap-4">
        <IconButton color="secondary" onClick={() => setPage('default')} className="tw-shrink-0">
          <ArrowLeftIcon size={16} />
        </IconButton>
        <h4 className="tw-m-0">{`Invite Settings for "${ticket.subject}"`}</h4>
      </div>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-m-0 tw-font-normal">Specify which assets are included for collaboration</span>
          <span className="tw-m-0 tw-font-normal">
            <strong>Note: </strong> Only shareable assets are shown below.
          </span>
        </div>
        <a
          href="https://help.designpickle.com/en/articles/8897988-share-review-links"
          target="_blank"
          rel="noopener noreferrer"
          className="tw-flex tw-items-center tw-gap-1 tw-text-cornflower-500"
        >
          Learn more
          <ExternalLinkIcon className="lu-sm" />
        </a>
      </div>

      {ticketFiles?.length > 0 && (
        <div className="tw-grid tw-grid-cols-5 tw-gap-2">
          {ticketFiles?.map((asset) => (
            <div
              key={asset.id}
              className={cn(
                'tw-relative tw-aspect-square tw-cursor-pointer tw-overflow-hidden tw-rounded-md tw-ring-2 tw-ring-neutral-300 tw-transition-all tw-duration-100 tw-ease-out hover:tw-ring-offset-2',
                selectedAssets.includes(asset.id) && 'tw-ring-cornflower-500',
              )}
              onClick={() => toggleAssetSelection(asset.id)}
            >
              <div className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2 tw-z-10">
                <Checkbox isChecked={selectedAssets.includes(asset.id)} onClick={() => {}} />
              </div>
              <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-neutral-100">
                <Tooltip content={asset.name} topOffset={10} className="tw-block tw-h-full tw-w-full">
                  <img
                    src={getPreviewUrl(asset.previewUrl)}
                    alt={asset.name}
                    className="tw-h-full tw-w-full tw-object-cover"
                  />
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="tw-flex tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2">
          {ticketFiles?.length > 0 && (
            <>
              <div>
                {selectedAssets.length} {selectedAssets.length === 1 ? 'asset' : 'assets'} selected
              </div>
              <button
                onClick={selectAllAssets}
                className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-cornflower-500"
              >
                {selectedAssets.length === ticketFiles?.length ? 'Deselect all' : 'Select all'}
              </button>
            </>
          )}
        </div>
        <Button
          color="purple"
          type="button"
          onClick={async () => {
            setIsUpdating(true)
            await updateSharedTicketFiles()
            setIsUpdating(false)
            setPage('default')
          }}
          disabled={selectedAssets.length === 0}
          className="tw-flex tw-items-center tw-gap-2"
        >
          <span>Update invite settings</span>
          {isUpdating && <LucideLoaderCircle className="tw-animate-spin" />}
        </Button>
      </div>
    </div>
  )
}
